import { observer } from 'mobx-react-lite'
import { FC, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { EmptyState } from '../../../features/empty-state'
import { Box } from '../core/Components'
import { useStatus } from '../core/Utils'
import { useAsyncEffect } from '../platform/explore/common/utils'
import { Loader } from '../platform/new-explore/components/Table'
import TableRow from '../platform/new-explore/components/TableRow'
import useSelectedMedia from '../platform/new-explore/hooks/useSelectedMedia'

const useFavorited = (walletAddress: string) => {
  const [status, setStatus] = useStatus()
  const [data, setData] = useState<IArpMedia[]>([])
  const [pageData, setPageData] = useState<{ currentPage: number; lastPage: number }>({
    currentPage: 0,
    lastPage: 1
  })
  const hasMore = pageData.currentPage < pageData.lastPage

  const handleSearch = async (fresh = false) => {
    const page = pageData.currentPage + 1
    const { media, meta } = await ArpRegistry.getFavoritesByWalletAddress(walletAddress, { page })
    const { lastPage, currentPage } = meta
    setPageData({ currentPage, lastPage })
    setData(fresh ? media : [...data, ...media])
  }

  useAsyncEffect(async () => {
    setStatus('loading')
    await handleSearch()
    setStatus('ready')
  }, [])

  const getNextPage = handleSearch

  return { data, hasMore, getNextPage, status }
}

const FavoritedTable: FC<{ walletAddress: string }> = ({ walletAddress }) => {
  const navigate = useNavigate()
  const { data, hasMore, getNextPage, status } = useFavorited(walletAddress)
  const queueData = useMemo(() => data.map((item) => item.id), [data])
  const { index, type, handleSelect } = useSelectedMedia({ queueData })

  const emptyStateOnClick = () => navigate('/home/songs')

  if (data.length === 0 && status === 'ready') {
    return (
      <EmptyState.Root>
        <EmptyState.Icon />
        <EmptyState.Text>No favorited sounds or songs yet!</EmptyState.Text>
        <EmptyState.ButtonRow>
          <EmptyState.PrimaryButton onClick={emptyStateOnClick}>
            Explore sounds and songs
          </EmptyState.PrimaryButton>
        </EmptyState.ButtonRow>
      </EmptyState.Root>
    )
  }
  return (
    <Box css={{ display: 'flex', alignItems: 'stretch', minHeight: '100vh' }}>
      <Box css={{ flex: 1, position: 'relative', marginTop: '16px' }}>
        <InfiniteScroll
          dataLength={data.length}
          hasMore={hasMore}
          loader={<Loader />}
          next={getNextPage}
        >
          {data.map((media, i) => (
            <TableRow
              media={media}
              key={media.id}
              onSelect={handleSelect}
              active={media.id === index}
            />
          ))}
        </InfiniteScroll>
      </Box>
    </Box>
  )
}

export default observer(FavoritedTable)
